import { useContentAvailability } from "@-/dev.improve.haniverse.lib.database.shards";
import { keepPreviousData, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import plurCount from "@-/com.npmjs.plur-count";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  ToolbarPopoverButton,
  ToolbarPopoverChoiceRow,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.popover";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import { cardReviewSettingsFromUser } from "@-/dev.improve.haniverse.lib.cards.card-content";
import {
  filterReviewEligibleDueUserCards,
  useDueUserCardsQueryOptions,
} from "@-/dev.improve.haniverse.lib.cards.study-card-data";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import {
  STUDY_ENVIRONMENTS,
  type StudyEnvironment,
  studyEnvironmentDescription,
  studyEnvironmentLabel,
  studyEnvironmentStudyMode,
} from "@-/dev.improve.haniverse.lib.learn.study-environment";
import { useVoiceStudyController } from "@-/dev.improve.haniverse.lib.voice-study";
import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import { useLearnCardsPathname } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.map-mode";
import { useDrillPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill.navigator";
import { useMemo } from "react";

import { studyEnvironmentIconName } from "#study-environment-icon-name";

/**
 * How many due reviews the queue will serve in this environment (and review focus) — the same
 * eligibility the next-due scheduler applies, so switching environment changes the count.
 * `undefined` until the first due-card scan resolves.
 */
function useStudyEnvironmentDueReviewCount(studyEnvironment: StudyEnvironment): number | undefined {
  const [reviewFocus] = useDeviceSetting("reviewFocus");
  const contentAvailability = useContentAvailability();
  // Both reads re-key on every write to their tables; keep the last count on screen meanwhile so
  // the line does not flicker after each rating.
  const { data: dueUserCards } = useQuery({
    ...useDueUserCardsQueryOptions(),
    placeholderData: keepPreviousData,
  });
  const { data: userSettings } = useQuery({
    ...actorQueries(import.meta.location).user.findFirst.useQueryOptions({}),
    placeholderData: keepPreviousData,
  });
  const cardReviewSettings = useMemo(
    () => cardReviewSettingsFromUser(userSettings),
    [userSettings],
  );
  return useMemo(
    () =>
      dueUserCards === undefined
        ? undefined
        : filterReviewEligibleDueUserCards(dueUserCards, cardReviewSettings, {
            contentAvailability,
            reviewFocus,
            studyEnvironment,
          }).length,
    [cardReviewSettings, dueUserCards, reviewFocus, contentAvailability, studyEnvironment],
  );
}

/**
 * The Learn More menu's Environment section: where the learner is studying, which decides the card
 * kinds the review queue serves and whether the deck runs in Touch or Voice mode. Hands-Free is the
 * only way into Voice mode here — choosing it starts voice study immediately and re-opens any
 * current review card by voice; choosing any other environment stops it and re-opens the card by
 * touch. Under the choices it says what the chosen environment changes and, when there are any, how
 * many reviews it leaves.
 */
export function LearnStudyEnvironmentPopoverButton() {
  const pathname = useLearnCardsPathname();
  const navigator = useDrillPageNavigator();
  const voiceStudyController = useVoiceStudyController();
  const [studyEnvironment, setStudyEnvironment] = useDeviceSetting("studyEnvironment");
  const [activeDeckCard] = useContextSelectors(LearnLayoutContext, (c) => c.activeDeckCard);
  const reviewCard =
    pathname.endsWith("/learn/deck") && activeDeckCard?.kind === "review" ? activeDeckCard : null;
  const label = studyEnvironmentLabel(studyEnvironment);
  const dueReviewCount = useStudyEnvironmentDueReviewCount(studyEnvironment);
  // Omit "0 left": with nothing to review there is no count to give.
  const leftLabel =
    dueReviewCount === undefined || dueReviewCount === 0
      ? undefined
      : `${plurCount(dueReviewCount, "review")} left`;
  const accessibilityLabel =
    leftLabel === undefined ? `Environment: ${label}` : `Environment: ${label}, ${leftLabel}`;

  const selectStudyEnvironment = (next: StudyEnvironment) => {
    setStudyEnvironment(next);
    const studyMode = studyEnvironmentStudyMode(next);
    if (studyMode === "voice") {
      void voiceStudyController.start();
    } else {
      voiceStudyController.stop();
    }
    if (reviewCard !== null && reviewCard.mode !== studyMode) {
      navigator.replace.mutate({
        contextSentence: reviewCard.contextSentence,
        drillType: reviewCard.drillType,
        grapheme: reviewCard.grapheme,
        studyMode,
      });
    }
  };

  return (
    <ToolbarPopoverButton
      accessibilityLabel={accessibilityLabel}
      label="Environment"
      name={studyEnvironmentIconName(studyEnvironment)}
      selected={studyEnvironment !== "standard"}
      testID="learn-study-environment-menu"
    >
      <ToolbarPopoverChoiceRow
        closeOnSelect
        label="Environment"
        onValueChange={selectStudyEnvironment}
        options={STUDY_ENVIRONMENTS.map((environment) => ({
          label: studyEnvironmentLabel(environment),
          testID: `learn-study-environment-${environment}`,
          value: environment,
        }))}
        value={studyEnvironment}
      />
      <Text className="text-xs text-muted-foreground" testID="learn-study-environment-description">
        {studyEnvironmentDescription(studyEnvironment)}
        {leftLabel === undefined ? "" : ` ${leftLabel}.`}
      </Text>
    </ToolbarPopoverButton>
  );
}
